import { ConcurrencyPool } from './ConcurrencyPool'
import { PartUrlProvider } from './PartUrlProvider'
import { loadSession, matchesFile, recordPart, saveSession, type PersistedPart } from './persistence'
import { putWithProgress } from './putWithProgress'
import type { UploadTransport } from './transport'
import type { UploadTicket } from './types'

export interface MultipartOptions {
  transport: UploadTransport
  /** Se persiste para poder listar las sesiones reanudables de este proyecto. */
  projectId: string
  signal?: AbortSignal
  partConcurrency?: number
  /** bytesSent acumulados del archivo entero, no de la parte. */
  onProgress?: (bytesSent: number) => void
  onPartComplete?: (completed: number, total: number) => void
}

/**
 * Sube un archivo grande por partes y cierra el multipart.
 *
 * La pieza que hace esto usable en Perú es la reanudación: cada parte
 * confirmada se persiste en IndexedDB **en el momento**, así que un corte
 * cuesta como mucho una parte (8 MiB) de trabajo repetido, no las dos horas
 * que puede durar una boda de 400 RAW.
 */
export async function uploadMultipart(
  file: File,
  ticket: UploadTicket,
  options: MultipartOptions,
): Promise<void> {
  const multipart = ticket.multipart

  if (!multipart) {
    throw new Error('El ticket no es multiparte.')
  }

  const { part_size_bytes: partSize, total_parts: totalParts } = multipart

  const done = new Map<number, string>()
  const persisted = await loadSession(ticket.session_id)

  // Sólo se reutilizan los ETags si es el MISMO archivo. El navegador no
  // conserva el File entre recargas, así que el usuario lo reselecciona a mano
  // y podría equivocarse: mezclar partes de dos ficheros produciría un objeto
  // corrupto que además pasaría la verificación de tamaño.
  if (persisted && matchesFile(persisted, file)) {
    for (const part of persisted.parts) {
      done.set(part.partNumber, part.etag)
    }
  }

  await saveSession({
    sessionId: ticket.session_id,
    assetId: ticket.asset_id,
    projectId: options.projectId,
    filename: file.name,
    fileSize: file.size,
    fileLastModified: file.lastModified,
    partSize,
    totalParts,
    parts: [...done].map(([partNumber, etag]): PersistedPart => ({ partNumber, etag })),
    updatedAt: Date.now(),
  })

  const urls = new PartUrlProvider(ticket.session_id, totalParts, options.transport)
  const pool = new ConcurrencyPool(options.partConcurrency ?? 4)

  // Progreso por parte: sin esto, la barra sólo avanzaría al terminar cada
  // trozo y parecería congelada durante minutos en un enlace lento.
  const partProgress = new Map<number, number>()
  const alreadyDone = [...done.keys()].reduce(
    (sum, partNumber) => sum + sizeOfPart(partNumber, partSize, totalParts, file.size),
    0,
  )

  const reportProgress = () => {
    const live = [...partProgress.values()].reduce((sum, bytes) => sum + bytes, 0)
    options.onProgress?.(alreadyDone + live)
  }

  const pending: Array<Promise<void>> = []

  for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
    if (done.has(partNumber)) continue

    const current = partNumber

    pending.push(
      pool.run(async () => {
        if (options.signal?.aborted) return

        const part = await urls.get(current)
        const [offset, length] = rangeOfPart(current, partSize, totalParts, file.size)
        const blob = file.slice(offset, offset + length)

        const etag = await putWithProgress(part.url, blob, {
          signal: options.signal,
          headers: part.headers,
          retries: 5,
          // Las partes SÍ necesitan ETag: es lo que cierra el multipart.
          requireEtag: true,
          onProgress: (sent) => {
            partProgress.set(current, sent)
            reportProgress()
          },
          onExpired: () => urls.refresh(current),
        })

        done.set(current, etag)
        partProgress.set(current, length)
        reportProgress()

        // Persistir AQUÍ, no al final: es lo que convierte un corte en 8 MiB
        // perdidos en lugar de la subida entera.
        await recordPart(ticket.session_id, { partNumber: current, etag })
        options.onPartComplete?.(done.size, totalParts)
      }),
    )
  }

  // Promise.all y no pool.drain(): así los rechazos se propagan en vez de
  // quedar sin capturar, y la primera parte que falle aborta el archivo.
  await Promise.all(pending)

  if (options.signal?.aborted) return

  const parts = [...done]
    .map(([part_number, etag]) => ({ part_number, etag }))
    .sort((a, b) => a.part_number - b.part_number)

  await options.transport.completeUpload(ticket.session_id, parts)
}

/** La última parte es más corta; el resto miden exactamente lo mismo (lo exige R2). */
function sizeOfPart(partNumber: number, partSize: number, totalParts: number, fileSize: number): number {
  return partNumber === totalParts ? fileSize - (totalParts - 1) * partSize : partSize
}

function rangeOfPart(
  partNumber: number,
  partSize: number,
  totalParts: number,
  fileSize: number,
): [number, number] {
  return [(partNumber - 1) * partSize, sizeOfPart(partNumber, partSize, totalParts, fileSize)]
}
